/**
 * Functional glyphs only.
 *
 * The decorative icon set that used to live here — mirrors, sparkles, shields,
 * flames, hangers — was clip-art standing in for pictures. The pictures now
 * exist (`marketing/photos.ts`), so the drawings are gone.
 *
 * What is left is the stuff a person has to click or read as state: arrows,
 * close, hamburger, tick, dash, plus, and the three social marks. All of them
 * are 1px hairlines on a 24×24 grid, inheriting `currentColor`.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function ArrowRight(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 12h18" />
      <path d="m14.5 5.5 6.5 6.5-6.5 6.5" />
    </Icon>
  );
}

export function ArrowDown(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3v18" />
      <path d="m5.5 14.5 6.5 6.5 6.5-6.5" />
    </Icon>
  );
}

export function Check(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m3.5 12.5 5.5 5.5L20.5 6" />
    </Icon>
  );
}

export function Dash(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12h16" />
    </Icon>
  );
}

export function Plus(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3v18" />
      <path d="M3 12h18" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 7h20" />
      <path d="M2 17h20" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m4 4 16 16" />
      <path d="m20 4-16 16" />
    </Icon>
  );
}

/* ------------------------------------------------------------------ *
 * Social. Outlines to match everything else — no filled blobs.
 * ------------------------------------------------------------------ */

export function InstagramIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <rect x="3" y="3" width="18" height="18" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="17.4" cy="6.6" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function TikTokIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M16.1 3h-2.7v12.2a2.6 2.6 0 1 1-2.2-2.6v-2.8a5.4 5.4 0 1 0 4.9 5.4V8.9a6.3 6.3 0 0 0 3.6 1.2V7.4a3.6 3.6 0 0 1-3.6-3.6V3Z" />
    </svg>
  );
}

export function XIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M17.3 3h3.1l-6.8 7.8L21.6 21h-6.2l-4.9-6.4L4.9 21H1.8l7.3-8.3L2.1 3h6.4l4.4 5.8L17.3 3Zm-1.1 16.1h1.7L7.9 4.8H6.1l10.1 14.3Z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Crop marks. A printer's device, not an illustration: two hairlines
 * meeting at a corner, used to register a section against the page.
 * ------------------------------------------------------------------ */

export function CropMark({ size = 12, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M0 6h12" />
      <path d="M6 0v12" />
    </svg>
  );
}
